import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Modal, Stack, Text } from '@mantine/core';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { StackedDialogContext } from '../../AnalysisWorkspace/components/primitives/stackedDialog.context';
import { useStackedDialogHost } from '../../AnalysisWorkspace/components/primitives/useStackedDialogHost';
import { INGEST_PANEL } from '../ingestion.constants';
import { AnalyseConfirm } from './components/AnalyseConfirm/AnalyseConfirm';
import { LibraryPanel } from './components/LibraryPanel/LibraryPanel';
import { PageCapNotice } from './components/PageCapNotice/PageCapNotice';
import { ProcessingPanel } from './components/ProcessingPanel/ProcessingPanel';
import { SourceFooter } from './components/SourceFooter/SourceFooter';
import { UploadPanel } from './components/UploadPanel/UploadPanel';
import { MODAL_HEIGHT, MODAL_WIDTH, MODAL_X_OFFSET } from './ExtractFromDocumentModal.styles';
import { useExtractFromDocumentModalController } from './useExtractFromDocumentModalController';
import {
	DS_AI_ELEVATION,
	DS_AI_PROPORTIONAL_FIGURES,
	DS_AI_TRANSITION_PROPS,
	dsAiScrimTransition,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';

interface Props {
	dealId: string;
	personId: string;
}

/** "Extract transaction from document", opened by `?ingest=` on the Transactions tab. */
export const ExtractFromDocumentModal = ({ dealId, personId }: Props) => {
	const tCapManage = useTranslationCapManage();
	const controller = useExtractFromDocumentModalController({ dealId, personId });
	const { panel, isFullScreen, batchId } = controller;
	const isProcessing = panel === INGEST_PANEL.PROCESSING && batchId !== null;
	const title = tCapManage('dealstack_ai.ingest.modal.title');
	// "Delete batch" asks in a dialog over this one; Escape then closes only that dialog.
	const stack = useStackedDialogHost(controller.onClose);

	return (
		<StackedDialogContext.Provider value={stack.host}>
			<Modal.Root
				opened={controller.opened}
				onClose={stack.onClose}
				centered
				size={MODAL_WIDTH}
				xOffset={MODAL_X_OFFSET}
				fullScreen={isFullScreen && !isProcessing}
				radius="md"
				zIndex={Z_INDEX.MODAL}
				transitionProps={DS_AI_TRANSITION_PROPS.bigModal}
				styles={{
					content: {
						display: 'flex',
						flexDirection: 'column',
						height: isFullScreen && !isProcessing ? '100vh' : MODAL_HEIGHT,
						boxShadow: DS_AI_ELEVATION.modal,
					},
				}}
			>
				<Modal.Overlay
					color={defaultTheme.v3.colors.dark[8]}
					backgroundOpacity={0.2}
					transitionProps={dsAiScrimTransition(controller.opened)}
				/>
				<Modal.Content
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_MODAL}
					aria-label={title}
				>
					<Stack
						flex={1}
						mih={0}
						gap={0}
						p={isProcessing ? 'lg' : 'sm'}
						styles={{ root: DS_AI_PROPORTIONAL_FIGURES }}
					>
						{isProcessing ? (
							<ProcessingPanel
								key={batchId}
								dealId={dealId}
								batchId={batchId}
								onClose={controller.onClose}
							/>
						) : (
							<Stack flex={1} gap="sm" mih={0} align="stretch" justify="flex-start">
								<Modal.Title>
									<Text
										component="span"
										m={0}
										fz="1.5rem"
										fw={600}
										lh="2rem"
										c="dark.8"
										tabIndex={-1}
										data-autofocus
										styles={{ root: { outline: 'none' } }}
									>
										{title}
									</Text>
								</Modal.Title>
								<Stack flex={1} mih={0} gap={0} bd="1px solid gray.4">
									<MantineTabs<INGEST_PANEL>
										value={panel}
										onTabChange={(value) => {
											if (
												value === INGEST_PANEL.UPLOAD ||
												value === INGEST_PANEL.LIBRARY
											) {
												controller.onSourceChange(value);
											}
										}}
										onlyDividerBorder
										keepMounted={false}
										tabs={[
											{
												label: tCapManage(
													'dealstack_ai.ingest.modal.tab_upload'
												),
												value: INGEST_PANEL.UPLOAD,
												icon: 'upload-v3-thin',
												testId: TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_UPLOAD_TAB,
											},
											{
												label: tCapManage(
													'dealstack_ai.ingest.modal.tab_library'
												),
												value: INGEST_PANEL.LIBRARY,
												icon: 'folder',
												testId: TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_LIBRARY_TAB,
											},
										]}
										panels={[
											{
												value: INGEST_PANEL.UPLOAD,
												content: (
													<UploadPanel uploads={controller.uploads} />
												),
											},
											{
												value: INGEST_PANEL.LIBRARY,
												content: (
													<LibraryPanel
														library={controller.library}
														isFullScreen={isFullScreen}
														onToggleFullScreen={
															controller.onToggleFullScreen
														}
													/>
												),
											},
										]}
									/>
									{controller.confirm && (
										<AnalyseConfirm confirm={controller.confirm} />
									)}
									{controller.pageCap && (
										<PageCapNotice notice={controller.pageCap} />
									)}
									<SourceFooter
										footer={controller.footer}
										onCancel={controller.onClose}
									/>
								</Stack>
							</Stack>
						)}
					</Stack>
				</Modal.Content>
			</Modal.Root>
		</StackedDialogContext.Provider>
	);
};
